import {
  FileAttachment,
  UploadSession,
  MailFolder,
  Message,
} from '@microsoft/microsoft-graph-types';
import { chunk, get, map } from 'lodash';

import { createGraphApi, GraphApiEndpoint } from '../factory';
import {
  BatchResponseSingleValue,
  ValueOf,
  ValueOfPaginated,
} from '../helper/helper';
import axios from 'axios';
import { PromiseQueue } from '../../utils/promiseQueue';

/**
 * Max file size for attachments via post request
 *
 * According to the documentation (https://learn.microsoft.com/en-us/graph/api/attachment-createuploadsession?view=graph-rest-1.0&tabs=csharp),
 * files smaller than 3 MB must be sent via POST and larger files via createUploadSession *
 */
const MAX_ATTACHMENT_SIZE_FOR_POST_BYTES = 3000000;

/**
 * Chunk size for every file upload
 */
const UPLOAD_CHUNK_SIZE_BYTES = 500000;

interface MailFolderBeta extends MailFolder {
  wellKnownName: string; // Beta field
}

interface EmailGetResponse {
  record: Message;
  inlineAttachments: FileAttachment[];
}

interface EmailIndexResponse {
  emails: Message[];
  hasMore: boolean;
  page: number;
}

interface EmailSearchResult {
  searchTerms: string[];
  hitsContainers: EmailSearchHitsContainer[];
}

interface EmailSearchHitsContainer {
  total: number;
  moreResultsAvailable: boolean;
  hits: EmailSearchHit[];
}

interface EmailSearchHit {
  hitId: string;
  rank: number;
  summary: string;
  resource: Message;
}

interface Recipient {
  email: string;
  displayName: string;
}

interface RecipientSearchResult {
  mail: string;
  displayName: string;
}

export interface DeleteEmailsResult {
  deleted: Message[];
  failed: Message[];
}

interface FileAttachmentSelectIdAndContentId {
  id: string;
  contentId: string;
}

export enum AttachmentTypeFilter {
  All,
  Inline,
  NonInline,
}

class EmailService {
  /**
   * Loads all available email folders
   */
  static async getFolders(authToken: string): Promise<MailFolderBeta[]> {
    const doFetch = async (pathname: string) => {
      const response = await createGraphApi(
        authToken,
        GraphApiEndpoint.Beta,
      ).get<ValueOf<MailFolderBeta[]>>(pathname);

      return response;
    };

    let url = '/me/mailFolders';
    let folders: MailFolderBeta[] = [];
    do {
      const response = await doFetch(url);
      folders = [...folders, ...response.data.value];
      url = get(response.data, '@odata.nextLink', '');
      if (url) {
        // Clean pagination links
        const parsed = new URL(url);
        url = `${parsed.pathname.replace(/^\/beta/, '')}${parsed.search}`;
      }
    } while (url);

    return folders;
  }

  /**
   * Retrieves emails of the given folder.
   */
  static async getEmails(
    authToken: string,
    folder: MailFolder,
    page: number,
  ): Promise<EmailIndexResponse> {
    const pageSize = 20;
    const response = await createGraphApi(authToken, GraphApiEndpoint.Beta).get<
      ValueOfPaginated<Message[]>
    >(
      `/me/mailFolders/${
        folder.id
      }/messages?$orderby=receivedDateTime desc&$expand=attachments($select=id,name,contentType,size,isInline)&$top=${pageSize}&$skip=${
        page * pageSize
      }`,
    );

    const emails = response.data.value;

    return {
      emails,
      hasMore: !!response.data['@odata.nextLink'],
      page,
    };
  }

  /**
   * Retrieves a single email message record and it's inline attachments
   * including the attachment contents. Used for rendering a single
   * email message and all inline attachments (ie. images).
   */
  static async getEmail(
    authToken: string,
    email: Message,
  ): Promise<EmailGetResponse> {
    const response = await createGraphApi(authToken).get<Message>(
      `/me/messages/${email.id}?$expand=attachments($select=id,name,contentType,size,isInline)`,
    );
    const record: Message = response.data;
    const inlineAttachments: FileAttachment[] =
      await EmailService.getAttachments(
        authToken,
        record,
        AttachmentTypeFilter.Inline,
      );

    const result = {
      record,
      inlineAttachments,
    };

    return result;
  }

  /**
   * Loads attachments with all available meta data and file contents
   * for the given email message.
   */
  static async getAttachments(
    authToken: string,
    email: Message,
    type: AttachmentTypeFilter = AttachmentTypeFilter.All,
  ): Promise<FileAttachment[]> {
    if (
      // hasAttachments does not account for inline attachments
      email.attachments === undefined ||
      email.attachments === null ||
      email.attachments.length === 0
    ) {
      return [];
    }

    const ids = map(
      email.attachments.filter(attachment => {
        switch (type) {
          case AttachmentTypeFilter.Inline:
            return attachment.isInline;
          case AttachmentTypeFilter.NonInline:
            return attachment.isInline === false;
          default:
            return true;
        }
      }),
      'id',
    );

    const attachments: FileAttachment[] = [];

    for (const pice of chunk(ids)) {
      const payload = {
        requests: pice.map(id => ({
          id,
          method: 'GET',
          url: `/me/messages/${email.id}/attachments/${id}`,
        })),
      };

      const batchResponse = await createGraphApi(authToken).post<
        BatchResponseSingleValue<FileAttachment>
      >('/$batch', payload);

      for (const singleResponse of batchResponse.data.responses) {
        if (singleResponse.status !== 200) {
          continue;
        }

        attachments.push(singleResponse.body);
      }
    }

    return attachments;
  }

  /**
   * Updates the fields defined by changeset of the given message record.
   */
  static async patchEmail(
    authToken: string,
    emailItem: Message,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    changes: { [k: string]: any },
  ): Promise<Message> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const body: { [key: string]: any } = {
      '@odata.context': get(emailItem, '@odata.context'),
      '@odata.etag': get(emailItem, '@odata.etag'),
      ...changes,
    };

    const response = await createGraphApi(authToken).patch<Message>(
      `/me/messages/${emailItem.id}`,
      body,
    );

    return response.data as Message;
  }

  /**
   * Creates an empty message record. The message is automatically
   * marked as draft and moved to the drafts email folder.
   */
  static async createDraft(authToken: string): Promise<Message> {
    const request = createGraphApi(authToken);
    const response = await request.post<Message>(
      '/me/messages',
      {},
      {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );

    return response.data;
  }

  /**
   * Deletes a single email.
   *
   * @static
   * @param {string} authToken
   * @param {Message} email
   * @return {*}  {Promise<boolean>} True when the message has been deleted
   * @memberof EmailService
   */
  static async deleteEmail(
    authToken: string,
    email: Message,
  ): Promise<boolean> {
    try {
      const response = await createGraphApi(authToken).delete<void>(`/me/messages/${email.id}`);
      return response.status === 204;
    } catch {
      return false;
    }
  }

  /**
   * Deletes multiple emails one after another and reports which of them
   * could not be deleted. Requests are not parallelized to stay below the
   * Graph throttling limits.
   *
   * @static
   * @param {string} authToken
   * @param {Message[]} emails
   * @return {*}  {Promise<DeleteEmailsResult>}
   * @memberof EmailService
   */
  static async deleteEmails(authToken: string, emails: Message[]): Promise<DeleteEmailsResult> {
    const result: DeleteEmailsResult = { deleted: [], failed: [] };

    for (const email of emails) {
      const success = await EmailService.deleteEmail(authToken, email);
      if (success) {
        result.deleted.push(email);
      } else {
        result.failed.push(email);
      }
    }

    return result;
  }

  /**
   * Deletes a mail folder including all of its subfolders and messages.
   * Folders with a wellKnownName are owned by Outlook and cannot be deleted.
   *
   * @static
   * @param {string} authToken
   * @param {string} folderId
   * @return {*}  {Promise<boolean>} True when the folder has been deleted
   * @memberof EmailService
   */
  static async deleteFolder(authToken: string, folderId: string): Promise<boolean> {
    const response = await createGraphApi(authToken).delete<void>(`/me/mailFolders/${folderId}`);

    return response.status === 204;
  }

  /**
   * Get the attachment id by a given content id for a message draft
   *
   * @private
   * @static
   * @param {Message} draft
   * @param {string} authToken
   * @param {string} contentId
   * @return {*}  {Promise<string>}
   * @memberof EmailService
   */
  private static async getAttachmentIdByContentId(
    draft: Message,
    authToken: string,
    contentId: string,
  ): Promise<string> {
    const getAttachmentsResponse = await createGraphApi(authToken).get<
      ValueOf<FileAttachmentSelectIdAndContentId[]>
    >(
      `/me/messages/${draft.id}/attachments/?$select=id,microsoft.graph.fileAttachment/contentId`,
    );

    let attachmentId = '';
    getAttachmentsResponse.data.value.forEach(attachment => {
      if (attachment.contentId === contentId) {
        attachmentId = attachment.id;
      }
    });

    if (attachmentId === '') {
      throw new Error('Attachment ID not found');
    }

    return attachmentId;
  }

  private static addDraftAttachmentsInProgress: string[] = [];

  /**
   * Cancels the upload of a draft attachment. This method should be called when the user removes an uploading attachment from the composer.
   *
   * @static
   * @param {string} contentId
   * @return {*}  {Promise<void>}
   * @memberof EmailService
   */
  static async cancelAddDraftAttachment(contentId: string): Promise<void> {
    EmailService.removeDraftAttachmentFromInProgress(contentId);
  }

  /**
   * Removes the attachment from the in progress list. This method should be called when the upload of an attachment is finished or cancelled.
   *
   * @private
   * @static
   * @param {string} constentId
   * @return {*}  {Promise<void>}
   * @memberof EmailService
   */
  private static async removeDraftAttachmentFromInProgress(
    constentId: string,
  ): Promise<void> {
    const index =
      EmailService.addDraftAttachmentsInProgress.indexOf(constentId);
    if (index !== -1) {
      EmailService.addDraftAttachmentsInProgress.splice(index, 1);
    }
  }

  /**
   * Queueing of uploading draft attachments to avoid api errors and speed up uploads a bit
   */
  static addDraftAttachmentQueue = new PromiseQueue();

  /**
   * Adds an attachment to an existing message draft record.
   *
   * @param authToken
   * @param draft The message we want to add the attachment to
   * @param filename Filename of the attachment
   * @param fileType Attachment mimetype
   * @param fileContentBlob file content of the attachment as Blob
   * @param contentId Content ID of the attachment
   */
  static async addDraftAttachment(
    authToken: string,
    draft: Message,
    filename: string,
    fileType: string,
    fileContentBlob: Blob,
    contentId: string,
  ): Promise<string | boolean> {
    EmailService.addDraftAttachmentsInProgress.push(contentId);

    return EmailService.addDraftAttachmentQueue.enqueue(
      () =>
        new Promise(resolve => {
          resolve(
            EmailService.addDraftAttachmentWorker(
              authToken,
              draft,
              filename,
              fileType,
              fileContentBlob,
              contentId,
            ),
          );
        }),
    );
  }

  /**
   * Woker to adds an attachment to an existing message draft record.
   *
   * @param authToken
   * @param draft The message we want to add the attachment to
   * @param filename Filename of the attachment
   * @param fileType Attachment mimetype
   * @param fileContentBlob file content of the attachment as Blob
   * @param contentId Content ID of the attachment
   */
  static async addDraftAttachmentWorker(
    authToken: string,
    draft: Message,
    filename: string,
    fileType: string,
    fileContentBlob: Blob,
    contentId: string,
  ): Promise<string | boolean> {
    if (EmailService.addDraftAttachmentsInProgress.indexOf(contentId) === -1) {
      return false;
    }

    const fileContentAsBase64 = await EmailService.getObjectBlobAsBase64(
      fileContentBlob,
    );

    if (fileContentBlob.size > MAX_ATTACHMENT_SIZE_FOR_POST_BYTES) {
      const createUploadSessionResponse = await createGraphApi(
        authToken,
      ).post<UploadSession>(
        `/me/messages/${draft.id}/attachments/createUploadSession`,
        {
          AttachmentItem: {
            attachmentType: 'file',
            name: filename,
            size: fileContentBlob.size,
            contentType: fileType,
            isInline: false,
            contentId: contentId,
          },
        },
      );

      const fragmentsTotalCount = Math.ceil(
        fileContentBlob.size / UPLOAD_CHUNK_SIZE_BYTES,
      );
      let fragmentsIndex = 0;
      let bytesRemaining = fileContentBlob.size;

      if (!createUploadSessionResponse.data.uploadUrl) {
        EmailService.removeDraftAttachmentFromInProgress(contentId);

        throw new Error('Upload URL is not available');
      }

      while (fragmentsIndex < fragmentsTotalCount) {
        if (
          EmailService.addDraftAttachmentsInProgress.indexOf(contentId) === -1
        ) {
          return false;
        }

        const step = (fragmentsIndex + 1) * UPLOAD_CHUNK_SIZE_BYTES;
        const from = fragmentsIndex * UPLOAD_CHUNK_SIZE_BYTES;
        const to =
          bytesRemaining > UPLOAD_CHUNK_SIZE_BYTES
            ? step - 1
            : fileContentBlob.size - 1;
        bytesRemaining = Math.max(fileContentBlob.size - step, 0);

        try {
          // This call must be without authentication,
          // thus we can't use createGraphApi(..)
          await axios.put(
            createUploadSessionResponse.data.uploadUrl,
            fileContentBlob.slice(from, to + 1),
            {
              headers: {
                'Content-Type': 'application/octet-stream',
                'Content-Range': `bytes ${from}-${to}/${fileContentBlob.size}`,
              },
            },
          );
        } catch (error) {
          EmailService.removeDraftAttachmentFromInProgress(contentId);

          throw error;
        }

        ++fragmentsIndex;
      }

      const attachmentId = await EmailService.getAttachmentIdByContentId(
        draft,
        authToken,
        contentId,
      );

      EmailService.removeDraftAttachmentFromInProgress(contentId);

      return attachmentId;
    } else {
      // Upload files less than 3 MB via POST request
      const response = await createGraphApi(authToken).post<FileAttachment>(
        `/me/messages/${draft.id}/attachments`,
        {
          '@odata.type': '#microsoft.graph.fileAttachment',
          contentBytes: fileContentAsBase64,
          contentType: fileType,
          name: filename,
          isInline: false,
        },
      );

      EmailService.removeDraftAttachmentFromInProgress(contentId);

      if (!response.data.id) {
        throw new Error('Attachment ID not found');
      }

      return response.data.id;
    }
  }

  static async deleteDraftAttachment(
    authToken: string,
    draft: Message,
    attachmentId: string,
  ): Promise<boolean> {
    const response = await createGraphApi(authToken).delete<void>(
      `/me/messages/${draft.id}/attachments/${attachmentId}`,
    );

    return response.status === 200 || response.status === 204;
  }

  /**
   * Loads a message draft with all attachments, including content bytes.
   */
  static async loadDraft(
    authToken: string,
    draft: Message,
  ): Promise<{ draft: Message; attachments: FileAttachment[] }> {
    const attachments = await EmailService.getAttachments(authToken, draft);

    return {
      draft,
      attachments,
    };
  }

  /**
   * Sends a message draft as email
   */
  static async sendDraft(authToken: string, draft: Message): Promise<boolean> {
    try {
      const response = await createGraphApi(authToken).post<Message>(
        `/me/messages/${draft.id}/send`,
        {},
      );
      return response.status === 200 || response.status === 202;
    } catch {
      return false;
    }
  }

  /**
   * Creates a new message draft that can be used as a reply
   */
  static async createReply(
    authToken: string,
    message: Message,
    all: boolean,
  ): Promise<Message> {
    const url = all
      ? `/me/messages/${message.id}/createReplyAll`
      : `/me/messages/${message.id}/createReply`;

    const response = await createGraphApi(authToken).post<Message>(url, {});

    return response.data;
  }

  static async createForward(
    authToken: string,
    message: Message,
  ): Promise<Message> {
    const response = await createGraphApi(authToken).post<Message>(
      `/me/messages/${message.id}/createForward`,
      {},
    );

    return response.data;
  }

  static async search(
    authToken: string,
    searchTerm: string,
  ): Promise<Message[]> {
    const response = await createGraphApi(authToken).post<
      ValueOf<EmailSearchResult[]>
    >('search/query', {
      requests: [
        {
          entityTypes: ['message'],
          query: {
            queryString: searchTerm,
          },
          from: 0,
          size: 30,
          enableTopResults: true,
        },
      ],
    });

    const resultsMap: { [key: string]: Message } = {};

    for (const value of get(response, 'data.value', [])) {
      for (const hitContainer of get(value, 'hitsContainers', [])) {
        for (const hit of get(hitContainer, 'hits', [])) {
          // Hit ids contains slashes, that must be replaced by - so that we can
          // make further requests referencing the id.
          const resourceId = hit.hitId.replaceAll('/', '-');

          // A single hit may be listed more than once
          if (get(resultsMap, resourceId, false) === false) {
            resultsMap[resourceId] = {
              ...hit.resource,
              id: resourceId,
            };
          }
        }
      }
    }

    return Object.values(resultsMap);
  }

  static async getRecipientSuggestions(
    authToken: string,
    searchTerm: string,
  ): Promise<Recipient[]> {
    const searchUsersUrl = `/users?$filter=startswith(displayName,'${searchTerm}') or startswith(mail,'${searchTerm}') or startswith(givenName,'${searchTerm}') or startswith(surName,'${searchTerm}')&$select=displayname,mail`;
    const searchGroupsUrl = `/groups?$filter=startswith(displayName,'${searchTerm}') or startswith(mail,'${searchTerm}')&select=displayname,mail`;

    let recipients: Recipient[] = [];

    for (const url of [searchUsersUrl, searchGroupsUrl]) {
      const response = await createGraphApi(
        authToken,
        GraphApiEndpoint.Beta,
      ).get<ValueOf<RecipientSearchResult[]>>(url);

      recipients = [
        ...recipients,
        ...response.data.value.map(result => ({
          email: result.mail,
          displayName: result.displayName,
        })),
      ];
    }

    return recipients;
  }

  static async fetchNewEmails(
    authToken: string,
    folderId: string,
    lastUpdate: string,
  ): Promise<Message[]> {
    const url = `/me/messages?$filter=receivedDateTime+gt+${lastUpdate}+and+parentFolderId+eq+'${folderId}'`;

    const response = await createGraphApi(authToken).get<ValueOf<Message[]>>(
      url,
    );

    return response.data.value;
  }

  /**
   * Returns a Blob as base 64 encoded string.
   */
  static getObjectBlobAsBase64 = (blob: Blob): Promise<string> => {
    return new Promise<string>(resolve => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result as string;
        const base64 = dataUrl.split(',')[1];
        resolve(base64);
      };
      reader.readAsDataURL(blob);
    });
  };
}

export { EmailService };
